import { Action } from 'utils'
import { isEqual } from 'lodash-es'
import { ofType, combineEpics, StateObservable } from 'redux-observable'
import { Observable, Scheduler, of, throwError, concat } from 'rxjs'
import { RouterService } from 'services'
import {
  ignoreElements,
  tap,
  map,
  switchMap,
  filter,
  takeUntil,
  catchError,
  repeatWhen,
  delay,
  retryWhen,
  distinctUntilChanged,
  take,
  withLatestFrom,
  mergeMap
} from 'rxjs/operators'
import { GlobalState } from 'store'
import { Locale, Translation } from 'locale'
import { IConnection } from 'messaging'
import { AuthActions, EventResponseAction, ServerActionTypes, EventResponseStatus, LogToServerActions } from 'api'
import { CredentialsStatus, ConnectionStatus, ConnectionActionTypes, ConnectionActions } from 'store/connection'
import {
  createConnectionDetailsSelector,
  createSignInDetailsSelector,
  createCombinedStatusSelector,
  CombinedStatus
} from 'selectors/connectionSelectors'
import { DiagnosticsDependencies } from './diagnosticsEpic'
import { BTQAction } from 'epics'

const RECONNECT_INTERVAL = 10000
const RECONNECT_RETRIES = 3

export interface ConnectionDependencies {
  connection: IConnection<any, any>
  routerService: RouterService
  scheduler?: Scheduler
}

// Keeps the connection connected to the server. It should not attempt to connect until the
// credentials are set, otherwise the connection could be closed due to lack of heartbeats
export function connectEpic(
  _action$: Observable<BTQAction>,
  state$: StateObservable<GlobalState>,
  dependencies: ConnectionDependencies & DiagnosticsDependencies
): Observable<BTQAction> {
  const combinedStatusSelector = createCombinedStatusSelector()
  const connectionDetailsSelector = createConnectionDetailsSelector()

  return state$.pipe(
    map(state => combinedStatusSelector(state)),
    distinctUntilChanged<CombinedStatus>(isEqual),
    filter(({ credentialsStatus }) => credentialsStatus === CredentialsStatus.Available),
    filter(({ connectionStatus }) => connectionStatus === ConnectionStatus.Disconnected),
    map(() => connectionDetailsSelector(state$.value)),
    switchMap(details => {
      dependencies.diagnosticsLogger.log(`Connect (connectEpic) ${JSON.stringify(details)}`)
      return of(1).pipe(
        switchMap(() => dependencies.connection.connect(details.connectionEndpoint, details.connectionTimeout)),
        takeUntil(state$.pipe(filter(state => state.connectionStore.credentialsStatus === CredentialsStatus.NotSet))),
        retryWhen(errored$ =>
          details.connectedOnce
            ? concat(
                errored$.pipe(delay(RECONNECT_INTERVAL, dependencies.scheduler), take(RECONNECT_RETRIES)),
                throwError(Locale.translate(Translation.SC_CONNECTION_FAILED_AFTER_RETRIES, { retries: RECONNECT_RETRIES }))
              )
            : throwError(Locale.translate(Translation.SC_CONNECTION_FAILED))
        ),
        catchError(error => {
          dependencies.diagnosticsLogger.log(`Connection Error (connectEpic) ${JSON.stringify(error)}`)
          return of(ConnectionActions.signOut(error.toString()))
        })
      )
    }),
    // when sequence completes resubscribe
    // this will be either be because of a sign out action or credential reset
    repeatWhen(connected$ => connected$)
  )
}

export function signInEpic(
  _action$: Observable<BTQAction>,
  state$: StateObservable<GlobalState>,
  dependencies: ConnectionDependencies & DiagnosticsDependencies
): Observable<BTQAction> {
  const combinedStatusSelector = createCombinedStatusSelector()
  const signInDetailsSelector = createSignInDetailsSelector()

  return state$.pipe(
    map(combinedStatusSelector),
    distinctUntilChanged<CombinedStatus>(isEqual),
    filter(({ credentialsStatus }) => credentialsStatus === CredentialsStatus.Available),
    filter(({ connectionStatus }) => connectionStatus === ConnectionStatus.Connected),
    map(() => signInDetailsSelector(state$.value)),
    tap(signInDetails => {
      if (signInDetails.username === null) {
        throw new Error('credentials marked as available but not set')
      }
      dependencies.diagnosticsLogger.log(`User signed in successfully ${JSON.stringify(signInDetails)}`)
    }),
    mergeMap(signInDetails => {
      const diagnosticsLog = dependencies.diagnosticsLogger.getLog()

      const diagnosticsActions = diagnosticsLog.map(logEntry =>
        LogToServerActions.logMessage(logEntry.severity, `${logEntry.timestamp} ${logEntry.message}`)
      )

      dependencies.diagnosticsLogger.clearLog()
      const actions = [...diagnosticsActions, AuthActions.login({ token: signInDetails.username! })]

      return of(...actions)
    })
  )
}

export function signInStatusEpic(
  action$: Observable<BTQAction>,
  state$: Observable<GlobalState>,
  dependencies: ConnectionDependencies & DiagnosticsDependencies
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, ServerActionTypes.EVENT_RESPONSE, EventResponseAction>(ServerActionTypes.EVENT_RESPONSE),
    withLatestFrom(state$),
    filter(([action, _state]) => action.payload.status === EventResponseStatus.FAILED),
    filter(
      ([action, state]) =>
        state.connectionStore.loginCorrelationId !== null && state.connectionStore.loginCorrelationId === action.payload.correlationId
    ),
    tap(([action]) => dependencies.diagnosticsLogger.log(`Signing out (signInStatusEpic) ${action.payload.messages.join('\\r\\n')}`)),
    map(([action, _state]) => ConnectionActions.signOut(action.payload.messages.join('\r\n')))
  )
}

type ConnectionSignOutAction = ReturnType<typeof ConnectionActions.signOut>

export function signOutEpic(
  action$: Observable<BTQAction>,
  _state$: Observable<GlobalState>,
  dependencies: ConnectionDependencies
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, ConnectionActionTypes.SIGN_OUT, ConnectionSignOutAction>(ConnectionActionTypes.SIGN_OUT),
    withLatestFrom(_state$),
    tap(([_, state]) => {
      if (state.connectionStore.connectionStatus !== ConnectionStatus.Disconnected) {
        dependencies.connection.outgoing$.complete()
      }
      dependencies.routerService.navigate('/logout')
    }),
    ignoreElements()
  )
}

export function socketStatusEpic(
  _action$: Observable<BTQAction>,
  _state$: Observable<GlobalState>,
  dependencies: ConnectionDependencies & DiagnosticsDependencies
): Observable<BTQAction> {
  return dependencies.connection.status$.pipe(
    tap(status => dependencies.diagnosticsLogger.log(`Socket status changed. New status ${JSON.stringify(status)}`)),
    map(status => ConnectionActions.socketStatus(status))
  )
}

export function sendMessageEpic(
  action$: Observable<Extract<BTQAction, Action<string, {}>>>,
  _state$: Observable<GlobalState>,
  dependencies: ConnectionDependencies
): Observable<BTQAction> {
  return action$.pipe(
    filter(action => (action.meta && action.meta.sendToServer) === true),
    tap(action => dependencies.connection.outgoing$.next(action)),
    ignoreElements()
  )
}

export function receiveMessageEpic(
  _action$: Observable<BTQAction>,
  _state$: Observable<GlobalState>,
  dependencies: ConnectionDependencies
): Observable<BTQAction> {
  return dependencies.connection.incoming$
}

export const connectionEpic = combineEpics(
  signOutEpic,
  socketStatusEpic,
  connectEpic,
  signInEpic,
  signInStatusEpic,
  sendMessageEpic,
  receiveMessageEpic
)
